import React, { useEffect, useRef, useState } from 'react';
import { geoMercator, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import InlineCapture from '../../../components/common/InlineCapture';
import { submitPublicContactInquiry } from '../../../services/api/publicContact';

/** Natural Earth 110m, vendored to public/geo — never hand-drawn geometry. */
const TOPO_URL = '/geo/countries-110m.json';
const ZA_ID = '710';

interface City {
  name: string;
  lng: number;
  lat: number;
  live: boolean;
  anchor: 'start' | 'end';
  dx: number;
  dy: number;
}

const CITIES: City[] = [
  { name: 'East London', lng: 27.91, lat: -33.02, live: true, anchor: 'start', dx: 12, dy: 4 },
  { name: 'Mdantsane', lng: 27.77, lat: -32.92, live: true, anchor: 'end', dx: -12, dy: -8 },
  { name: 'Gqeberha', lng: 25.6, lat: -33.96, live: false, anchor: 'end', dx: -12, dy: 4 },
  { name: 'Cape Town', lng: 18.42, lat: -33.92, live: false, anchor: 'start', dx: 12, dy: 4 },
  { name: 'Durban', lng: 31.03, lat: -29.86, live: false, anchor: 'start', dx: 12, dy: 4 },
  { name: 'Johannesburg', lng: 28.05, lat: -26.2, live: false, anchor: 'start', dx: 12, dy: 4 },
];

const W = 720;
const H = 560;

const CoverageMap: React.FC = () => {
  const [shapes, setShapes] = useState<{ za: Feature; others: Feature[] } | null>(null);
  const [failed, setFailed] = useState(false);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;

    fetch(TOPO_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`geometry ${res.status}`);
        return res.json();
      })
      .then((topo) => {
        if (cancelled.current) return;
        /* topojson types `feature()` as Feature | FeatureCollection depending on
           the object passed; countries is always a GeometryCollection. */
        const collection = feature(
          topo,
          topo.objects.countries,
        ) as unknown as FeatureCollection<Geometry>;
        const za = collection.features.find((f) => String(f.id) === ZA_ID);
        if (!za) throw new Error('South Africa missing from geometry');
        setShapes({
          za,
          others: collection.features.filter((f) => String(f.id) !== ZA_ID),
        });
      })
      .catch(() => {
        if (!cancelled.current) setFailed(true);
      });

    return () => {
      cancelled.current = true;
    };
  }, []);

  /* The map is decorative reinforcement — the authoritative coverage list is
     rendered as text beside it, so a failure here costs nothing. */
  if (failed) {
    return <p className="sw-map-fallback">Coverage map unavailable.</p>;
  }

  if (!shapes) {
    return <div className="sw-map-frame" aria-hidden="true" />;
  }

  const projection = geoMercator().fitExtent(
    [
      [26, 26],
      [W - 26, H - 26],
    ],
    shapes.za as never,
  );
  const path = geoPath(projection);
  const elPoint = projection([27.91, -33.02]);

  return (
    <svg className="sw-map-frame" viewBox={`0 0 ${W} ${H}`} role="img"
      aria-label="Map of South Africa. Live in East London and Mdantsane; expanding to Gqeberha, Durban, Cape Town and Johannesburg.">
      <defs>
        {/* Stops are styled in route.css so the glow resolves through
            --sw-signal rather than repeating a hex here. */}
        <radialGradient id="sw-coverage-glow">
          <stop className="sw-map-glow-in" offset="0%" />
          <stop className="sw-map-glow-out" offset="100%" />
        </radialGradient>
      </defs>

      <g className="sw-map-neighbours">
        {shapes.others.map((f, i) => (
          <path d={path(f as never) ?? undefined} key={String(f.id ?? i)} />
        ))}
      </g>

      <path className="sw-map-za" d={path(shapes.za as never) ?? undefined} />

      {elPoint && (
        <circle cx={elPoint[0]} cy={elPoint[1]} r={Math.min(W, H) * 0.34}
          fill="url(#sw-coverage-glow)" />
      )}

      {CITIES.map((city) => {
        const p = projection([city.lng, city.lat]);
        if (!p) return null;
        return (
          <g key={city.name}>
            {city.live ? (
              <>
                <circle className="sw-map-pulse" cx={p[0]} cy={p[1]} r={6} />
                <circle className="sw-map-live" cx={p[0]} cy={p[1]} r={5} />
              </>
            ) : (
              <circle className="sw-map-soon" cx={p[0]} cy={p[1]} r={3.5} />
            )}
            <text className={city.live ? 'sw-map-label' : 'sw-map-label sw-map-label-soon'}
              x={p[0] + city.dx} y={p[1] + city.dy} textAnchor={city.anchor}>
              {city.live ? city.name : `${city.name} · soon`}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

/**
 * Coverage requests go through the existing public contact inbox rather than a
 * bespoke list — the volume does not yet justify a second pipeline, and a real
 * person seeing "reaches me" requests is more useful than a silent table.
 */
const notifyMe = (email: string) =>
  submitPublicContactInquiry({
    name: 'Coverage waitlist',
    email,
    subject: 'Notify me when ShiftWagon reaches my area',
    message: `Coverage request from the homepage map. Contact ${email} when ShiftWagon expands to their area.`,
    sourcePage: typeof window === 'undefined' ? '' : window.location.href,
  });

/**
 * `#coverage` is the target for the estimate widget's coverage line — a visitor
 * who prices a route outside the live cities lands on the waitlist here rather
 * than on a let-down further down the page.
 */
const CoverageSection: React.FC = () => {
  return (
    <section className="sw-section sw-coverage sw-on-dark" id="coverage">
      <div className="container sw-coverage-grid">
        <div>
          <span className="sw-eyebrow">Coverage</span>
          <h2>
            Live in the Eastern Cape.
            <br />
            Rolling out from here.
          </h2>
          <p className="sw-coverage-copy">
            We started where we know the roads — East London and Mdantsane — and we&apos;re
            expanding city by city across South Africa.
          </p>

          <dl className="sw-coverage-list">
            <div>
              <dt>Live now</dt>
              <dd>East London · Mdantsane</dd>
            </div>
            <div>
              <dt>Coming soon</dt>
              <dd>Gqeberha · Durban · Cape Town</dd>
            </div>
          </dl>

          {/* Everyone outside the two live cities hits a dead end here. One
              field turns that into a queue we can actually open to. */}
          <InlineCapture
            buttonLabel="Notify me"
            label="Email address for coverage updates"
            onSubmit={notifyMe}
            placeholder="you@example.com"
            showLabel
            type="email"
          />
        </div>

        <CoverageMap />
      </div>
    </section>
  );
};

export default CoverageSection;
